// ISSUE-1041. The pure draft model behind the Change contest rules dialog:
// prefill the wizard's rules draft from /contest/get, compute the fields to
// send to /contest/update, and decide when the two dates lock.
//
// The diff compares BUILT configs (buildContestConfig on both sides), never raw
// drafts, so a field nobody touched is never sent, and a child value a parent
// switch turned off (a reuse cap once reuse is off, a revive count once revives
// are unlimited) is sent as null so the server clears it.
import {
  DEFAULT_WEEK_START_DAY,
  type typeWeekStartDay,
} from "@sportsmet/contest-engine";
import type { useContestGet } from "@sportsmet/contest-sdk/private";

import type { typeSurvivorRulesDraft } from "../createPool/survivorRulesDraft";

// The patch, dirty and date-lock rules are shared with the mobile change-rules
// screen (ISSUE-1314), so both clients send the same fields.
export {
  areContestDatesLocked,
  buildContestRulesPatch,
  CONTEST_RULES_PATCH_KEYS,
  isContestRulesDraftDirty,
  type typeContestRulesPatch,
} from "@sportsmet/contest-engine/rules-draft";

export type typeContestConfigRead = NonNullable<
  NonNullable<ReturnType<typeof useContestGet>["data"]>["data"]
>["config"];

function toWeekStartDay(value: number | null | undefined): typeWeekStartDay {
  if (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 7
  ) {
    return value as typeWeekStartDay;
  }
  return DEFAULT_WEEK_START_DAY;
}

export function survivorRulesDraftFromConfig(
  config: typeContestConfigRead,
): typeSurvivorRulesDraft {
  return {
    startDate: config.startDate,
    ...(config.pickSchedule
      ? { pickSchedule: structuredClone(config.pickSchedule) }
      : {}),
    // ISSUE-1095. A TBD contest's stored endDate is the computed season end,
    // not a date anyone picked, so the draft holds none.
    endDate: config.endDateMode === "tbd" ? "" : config.endDate,
    endDateMode: config.endDateMode,
    roundGrouping: config.roundGrouping,
    weekStartDay: toWeekStartDay(config.weekStartDay),
    roundLockTiming: config.roundLockTiming,
    requiredPicksPerRound: config.requiredPicksPerRound,
    pickBasis: config.pickBasis,
    spreadTiming: config.spreadTiming ?? "live",
    teamReuseAllowed: config.teamReuseAllowed,
    teamReuseLockoutDays: config.teamReuseLockoutDays ?? 0,
    teamReuseMaxCount: config.teamReuseMaxCount ?? null,
    advanceCriterion: config.advanceCriterion,
    livesAllowed: 1,
    skipsCount: 0,
    tiePolicy: config.tiePolicy,
    postponementPolicy: config.postponementPolicy,
    // A pool setting, not a contest rule; the edit dialog hides it.
    maxMembers: null,
    maxEntriesPerMember: config.maxEntriesPerMember,
    revivesAllowed: config.revivesAllowed,
    revivesAllowedCount: config.revivesAllowedCount ?? null,
    tiebreaker: config.tiebreaker,
    winPoints: config.winPoints,
    lossPoints: config.lossPoints,
  };
}
